import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
  Cpu, Bot, FileText, LineChart, MessageSquare, 
  Settings, ShieldCheck, Workflow, ArrowRight, ChevronRight, 
  CheckCircle2, Network, Sparkles 
} from 'lucide-react';
import AiAutomationImg from '../../assets/AiAutomation.png'; // Assuming you have an image in assets

const CONTAINER = 'w-full max-w-7xl xl:max-w-[95%] 2xl:max-w-[85vw] mx-auto px-6 sm:px-8 lg:px-8';
const THEME_COLOR = '#10b981'; // Emerald theme for AI & Automation

/* ============================== PAGE DATA ============================== */

const FEATURES = [
  { 
    title: 'AI Chatbots', 
    icon: Bot, 
    desc: 'Deploy intelligent conversational agents for 24/7 customer support, ticketing, and lead generation.' 
  },
  { 
    title: 'AI Analytics', 
    icon: LineChart, 
    desc: 'Harness machine learning models to forecast trends, identify anomalies, and analyze complex business data.' 
  },
  { 
    title: 'OCR & Document Intelligence', 
    icon: FileText, 
    desc: 'Automatically extract and process data from invoices, receipts, identity documents, and unstructured text.' 
  },
  { 
    title: 'RPA Automation', 
    icon: Cpu, 
    desc: 'Automate repetitive, rule-based tasks across multiple software applications flawlessly and continuously.' 
  },
  { 
    title: 'Predictive Maintenance', 
    icon: Settings, 
    desc: 'Anticipate equipment failures before they occur using IoT sensor data and AI algorithms.' 
  },
  { 
    title: 'NLP & Sentiment Analysis', 
    icon: MessageSquare, 
    desc: 'Analyze customer interactions, emails, and online reviews to accurately gauge sentiment and intent.' 
  }
];

const AiAutomation = () => {
  // Use location hook to get routing state
  const location = useLocation();
  const navSource = location.state?.from === 'services' ? 'services' : 'solutions';

  return (
    <div className="overflow-x-hidden pt-0">
      <style>{`
        @keyframes seFadeUp { from{opacity:0;transform:translateY(24px)} to{opacity:1;transform:translateY(0)} }
        .se-fade-up { animation: seFadeUp .7s ease-out both; }
        @keyframes seFloat { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-12px)} }
        .se-float { animation: seFloat 6s ease-in-out infinite; }
        @media (prefers-reduced-motion: reduce) { .se-float, .se-fade-up { animation: none !important; } }
      `}</style>

      {/* ================= HERO SECTION ================= */}
      <section className="relative overflow-hidden bg-gradient-to-b from-[#ecfdf5] via-[#f8fafc] to-white">
        <div className="pointer-events-none absolute -top-24 -right-24 w-96 h-96 rounded-full bg-primary/10 blur-3xl" />
        <div className="pointer-events-none absolute top-40 -left-24 w-80 h-80 rounded-full bg-[#10b981]/10 blur-3xl" />
        
        <div className={`${CONTAINER} relative py-5 lg:py-6`}>
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-8 items-start">
            
            {/* LEFT SIDE: Text Content */}
            <div className="max-w-2xl relative z-10 se-fade-up">
              
              {/* Breadcrumb Navigation */}
              <nav className="flex flex-wrap items-center gap-2 text-sm text-slate-500 mb-6">
                <Link to="/" className="hover:text-primary transition-colors">Home</Link>
                <ChevronRight className="w-3.5 h-3.5 text-slate-400" />
                <Link to={`/${navSource}`} className="hover:text-primary transition-colors capitalize">{navSource}</Link>
                <ChevronRight className="w-3.5 h-3.5 text-slate-400" />
                <span className="text-primary font-medium">AI & Automation Solutions</span>
              </nav>

              <span className="inline-flex items-center gap-2 text-xs font-bold tracking-[0.18em] uppercase text-primary bg-white ring-1 ring-primary/25 px-4 py-1.5 rounded-full mb-6">
                <Cpu className="w-4 h-4 text-[#10b981]" /> SmartEdz Intelligence
              </span>
              <h1 className="text-4xl sm:text-5xl xl:text-[3.7rem] font-extrabold leading-[1.08] tracking-tight mb-6" style={{ color: '#13294b' }}>
                Intelligent AI & Automation Solutions
              </h1>
              <p className="text-lg text-slate-500 max-w-xl leading-relaxed mb-8">
                Transform your business with cutting-edge artificial intelligence and robotic process automation. Streamline workflows, extract actionable insights, and build smarter digital experiences.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/request-demo" className="inline-flex items-center gap-2 rounded-lg px-7 py-3.5 font-semibold text-white shadow-lg shadow-primary/30 bg-primary hover:brightness-110 transition">
                  Explore Capabilities <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>

            {/* RIGHT SIDE: Image replacing mockups */}
            <div className="relative se-fade-up w-full" style={{ animationDelay: '.15s' }}>
              <div className="absolute inset-x-8 top-4 bottom-16 bg-gradient-to-tr from-[#10b981]/15 to-[#34d399]/15 blur-3xl rounded-[48px]" />
              <img 
                src={AiAutomationImg} 
                alt="AI and Automation Dashboard Illustration" 
                className="relative z-10 w-full h-auto object-contain mix-blend-multiply hover:scale-[1.02] transition-transform duration-500"
              />
            </div>

          </div>
        </div>
      </section>

      {/* ================= CORE MODULES ================= */}
      <section className="bg-gradient-to-b from-[#f5f9fd] to-white py-14">
        <div className={CONTAINER}>
          <div className="text-center mb-10">
            <span className="block text-xs font-bold tracking-[0.2em] uppercase text-primary mb-3">Core Modules</span>
            <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight" style={{ color: '#13294b' }}>Everything you need in one dashboard</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {FEATURES.map((feature, index) => (
              <div key={index} className="group h-full flex flex-col bg-white rounded-2xl ring-1 ring-slate-100 p-6 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4 transition-transform group-hover:scale-110" style={{ backgroundColor: `${THEME_COLOR}1A` }}>
                  <feature.icon className="w-6 h-6" style={{ color: THEME_COLOR }} />
                </div>
                <h3 className="font-bold text-slate-800 text-lg mb-3">{feature.title}</h3>
                <p className="text-[13px] text-slate-500 leading-relaxed flex-1">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ================= INDUSTRY BENEFITS ================= */}
      <section className="py-14 bg-white">
        <div className={CONTAINER}>
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="se-fade-up">
              <span className="block text-xs font-bold tracking-[0.2em] uppercase text-[#10b981] mb-3">
                Why SmartEdz?
              </span>
              <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight mb-6" style={{ color: '#13294b' }}>
                Built to eliminate bottlenecks and accelerate scale
              </h2>
              <p className="text-slate-500 mb-8 leading-relaxed">
                Manual, repetitive tasks drain your team's energy and introduce human error. Our AI and automation solutions integrate directly into your existing ecosystem, allowing your workforce to focus on high-value strategic initiatives.
              </p>
              <div className="space-y-6">
                {[
                  { title: 'Scalable Intelligence', desc: 'Deploy machine learning models and automated scripts that scale effortlessly alongside your business operations.', icon: Network },
                  { title: 'Seamless Integration', desc: 'Connect automation bots and AI algorithms directly with your legacy ERPs, CRMs, and third-party APIs.', icon: Workflow },
                  { title: 'Data Privacy First', desc: 'Enterprise-grade security infrastructure ensuring your proprietary data and AI models never leave your control.', icon: ShieldCheck }
                ].map((item, i) => (
                  <div key={i} className="flex gap-4">
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0 bg-[#10b981]/10 text-[#10b981]">
                      <item.icon className="w-6 h-6" />
                    </div>
                    <div>
                      <h4 className="font-bold text-slate-800 text-lg">{item.title}</h4>
                      <p className="text-sm text-slate-500 mt-1">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="relative rounded-2xl bg-slate-50 border border-slate-100 p-8 shadow-inner se-fade-up" style={{ animationDelay: '0.2s' }}>
              <div className="absolute top-0 right-0 w-64 h-64 bg-[#10b981]/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
              <h3 className="font-bold text-slate-800 mb-6 flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-[#10b981]" /> Automation Impact
              </h3>
              <div className="space-y-4 relative z-10">
                {['Human Error Reduced', 'Processing Time Slashed', '24/7 Operations Live'].map((label, idx) => (
                  <div key={idx} className="bg-white p-4 rounded-xl shadow-sm border border-slate-100 flex items-center justify-between">
                    <span className="font-semibold text-slate-700 text-sm">{label}</span>
                    <span className="flex items-center gap-1 text-xs font-bold text-[#10b981] bg-[#10b981]/10 px-2 py-1 rounded-md">
                      <CheckCircle2 className="w-3 h-3" /> Verified
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ================= BOTTOM CTA ================= */}
      <section className="py-16">
        <div className={CONTAINER}>
          <div className="relative rounded-[2rem] overflow-hidden bg-gradient-to-br from-[#13294b] to-[#1e447a] p-10 sm:p-16 text-center">
            <div className="absolute top-0 right-0 w-96 h-96 bg-[#10b981]/20 rounded-full blur-3xl translate-x-1/3 -translate-y-1/3" />
            <div className="absolute bottom-0 left-0 w-96 h-96 bg-[#10b981]/10 rounded-full blur-3xl -translate-x-1/3 translate-y-1/3" />
            
            <div className="relative z-10 max-w-2xl mx-auto">
              <h2 className="text-3xl sm:text-4xl font-extrabold text-white mb-4">
                Ready to automate your workflows?
              </h2>
              <p className="text-slate-300 text-lg mb-8">
                Join forward-thinking enterprises that use SmartEdz to deploy AI chatbots, extract document data, and automate repetitive tasks.
              </p>
              <div className="flex flex-wrap items-center justify-center gap-4">
                <Link to="/request-demo" className="inline-flex items-center gap-2 rounded-lg px-8 py-4 font-bold text-[#13294b] bg-white hover:bg-slate-50 transition shadow-lg hover:-translate-y-1">
                  Schedule a Free Consultation <ArrowRight className="w-4 h-4" />
                </Link>
                <Link to="/contact-us" className="inline-flex items-center gap-2 rounded-lg px-8 py-4 font-bold text-white bg-white/10 hover:bg-white/20 transition ring-1 ring-white/20">
                  Talk to an Expert
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AiAutomation;